import classNames from "classnames";
import classes from "../Milestones/Milestones.module.scss";
import React, { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { Milestone, MilestoneColumnItem } from "../types";
import { ReactBootstrapHTMLContainerProps } from "../../../types/htmlElementTypes";
import useWindowDimensions from "../../hooks/useWindowDimension";
import Illustration from "../../../components/utilities/Illustration/Illustration"
import LogoWhiteSVG from "../../../public/logo-white.svg";
import { translatedMilestones } from "../Milestones/MilestonesData";
import { useTranslation } from "next-i18next";

interface Props extends ReactBootstrapHTMLContainerProps {
  variant: "desktop" | "mobile";
  scrollPercentage: number;
  pageResolutionChanged: boolean;
}

const MilestoneRenderer = ({
  className,
  scrollPercentage,
  variant,
  pageResolutionChanged,
  ...htmlProps
}: Props) => {
  const { t } = useTranslation("history");
  const { width } = useWindowDimensions();
  const milestones = translatedMilestones(t);

  const smallOrLower: boolean = width < 768;
  const milestoneRefs = useRef<Array<HTMLDivElement | null>>([]);
  let milestoneObserver: IntersectionObserver;

  const leftMilestoneColumn: MilestoneColumnItem[] = [];
  const rightMilestoneColumn: MilestoneColumnItem[] = [];
  const mobileMilestoneColumn: MilestoneColumnItem[] = [];

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    milestoneObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const entryTarget = entry.target as HTMLElement;
            const parentElement = entryTarget.parentElement;
            const childOfParentElement: Element | undefined =
              parentElement?.querySelectorAll(":scope > div")[1];

            switch (childOfParentElement?.className) {
              case classes.milestones__dot:
                childOfParentElement.classList.add(
                  classes.milestones__animationDot
                );
                break;

              case classes.milestones__lineLeft:
                childOfParentElement.classList.add(
                  classes.milestones__animationLineLeft
                );
                break;

              case classes.milestones__lineRight:
                childOfParentElement.classList.add(
                  classes.milestones__animationLineRight
                );
                break;

              default:
                break;
            }

            if (entryTarget.dataset.position === "left") {
              entryTarget.classList.add(
                classes.milestones__animationMilestoneLeft
              );
            } else {
              entryTarget.classList.add(
                classes.milestones__animationMilestoneRight
              );
            }
          }
        });
      },
      {
        threshold: 1,
      }
    );

    if (milestoneRefs.current) {

      for (let i = 0; i < milestoneRefs.current.length; i++) {
        const milestoneElement = milestoneRefs.current[i] as Element | null;
        if (milestoneElement) {
          milestoneObserver.observe(milestoneElement);
        }
      }
    }

    return () => {
      milestoneObserver.disconnect();
    };
  }, [milestoneRefs]);

  const renderMilestoneLine = (position: string) => (
    <div className={classNames(position === "left" ? classes.milestones__lineLeft : classes.milestones__lineRight)}></div>
  )

  const milestoneValueHandler = (
    id: number,
    milestone: Milestone,
    position: "left" | "right"
  ) => {
    if (typeof milestone.value === "string") {
      return (
        <div
          className={classNames(
            "d-flex align-items-center flex-row-reverse flex-lg-row",
            position === "left"
              ? "justify-content-end"
              : "justify-content-between"
          )}
        >
          <div
            className={classNames(
              classes.milestonesTextation,
              pageResolutionChanged && classes.milestonesRendered
            )}
            id={"milestone-" + id}
            key={id}
            ref={(el) => (milestoneRefs.current[id] = el)}
            data-position={position}
            data-type="textation"
          >
            {!smallOrLower &&
              !!milestone.icon &&
              <div className={classNames(position === "left" ? classes.milestones__iconLeft : classes.milestones__iconRight)}>
                {milestone.icon}
              </div>}

            {milestone.illustration ? (
              <div className="d-flex flex-column justify-content-evenly h-100 px-2">
                <div>
                  <Illustration className="mx-auto my-6 w-100 h-75" />
                </div>
                <div className={classes.milestones__illustration}>
                  <LogoWhiteSVG />
                </div>
              </div>
            ) : (
              <p className={classNames("m-0 py-5 px-5 w-100")}>
                {milestone.value}
              </p>
            )}
          </div>
          {renderMilestoneLine(position)}
        </div>
      );
    }

    if (typeof milestone.value === "number") {
      return (
        <div
          className={classNames(
            "d-flex align-items-center flex-row-reverse flex-lg-row w-100",
            position === "left"
              ? "justify-content-end"
              : "justify-content-between"
          )}
        >
          <div
            className={classNames(
              classes.milestonesYear,
              pageResolutionChanged && classes.milestonesRendered
            )}
            id={"milestone-" + id}
            key={id}
            ref={(el) => (milestoneRefs.current[id] = el)}
            data-position={position}
            data-type="year"
          >
            <p
              className={classNames(
                "m-0",
                position === "left" ? "text-end" : "text-start"
              )}
            >
              {milestone.value}
            </p>
          </div>
          <div
            className={classNames(
              classes.milestones__dot,
              pageResolutionChanged && classes.milestones__dotRendered
            )}
          ></div>
        </div>
      );
    }


    return (
      <div
        className={classNames(
          "d-flex align-items-center flex-row-reverse flex-lg-row",
          position === "left"
            ? "justify-content-end"
            : "justify-content-between"
        )}
      >
        <div
          className={classNames(
            classes.milestonesImage,
            pageResolutionChanged && classes.milestonesRendered,
          )}
          id={"milestone-" + id}
          key={id}
          ref={(el) => (milestoneRefs.current[id] = el)}
          data-position={position}
          data-type="image"
        >
          {milestone.value}
        </div>
        {position === "left" ? (
          <div
            className={classNames(
              classes.milestones__lineLeft,
              pageResolutionChanged && classes.milestones__lineRendered
            )}
          ></div>
        ) : (
          <div
            className={classNames(
              classes.milestones__lineRight,
              pageResolutionChanged && classes.milestones__lineRendered
            )}
          ></div>
        )}
      </div>
    );
  };

  milestones.forEach((milestone, index) => {
    const currentMilestoneItem: MilestoneColumnItem = {
      id: index,
      milestone: milestone,
    };
    mobileMilestoneColumn.push(currentMilestoneItem);
    if (index % 2 === 0) {
      leftMilestoneColumn.push(currentMilestoneItem);
    } else {
      rightMilestoneColumn.push(currentMilestoneItem);
    }
  });

  const desktopElements = (
    <Row className="d-flex position-relative justify-content-between">
      <Col md={5} className="d-flex flex-column align-items-start">
        {leftMilestoneColumn.map((milestone) =>
          milestoneValueHandler(milestone.id, milestone.milestone, "left")
        )}
      </Col>
      <Col md={2} className="position-relative w-auto">
        <div
          className={classes.milestones__verticalLine}
          style={{
            height: `${scrollPercentage}%`,
          }}
        ></div>
      </Col>
      <Col md={5} className="d-flex flex-column align-items-end mt-10 pt-10">
        {rightMilestoneColumn.map((milestone) =>
          milestoneValueHandler(milestone.id, milestone.milestone, "right")
        )}
      </Col>
    </Row>
  );

  const mobileElements = (
    <Row className="d-flex position-relative justify-content-between">
      <div className="d-flex position-absolute w-auto h-100">
        <div
          className={classes.milestones__verticalLine}
          style={{
            height: `${scrollPercentage}%`,
          }}
        ></div>
      </div>
      <Col xs={12} className="d-flex flex-column">
        {mobileMilestoneColumn.map((milestone) =>
          milestoneValueHandler(milestone.id, milestone.milestone, "right")
        )}
      </Col>
    </Row>
  );
  return (
    <div {...htmlProps}>
      {variant === "desktop" ? desktopElements : mobileElements}
    </div>
  );
};

export default MilestoneRenderer;
